import {
  CommonModule,
  CurrencyPipe,
  DecimalPipe,
  registerLocaleData,
} from '@angular/common';
import { HttpClient } from '@angular/common/http';
import localeHu from '@angular/common/locales/hu';
import { LOCALE_ID, ModuleWithProviders, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { RouterModule } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownModule, MarkedOptions } from 'ngx-markdown';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { SwiperModule } from 'swiper/angular';

import { OrderListEffects } from '@app/order-list/store/order-list.effects';
import { OrderListFeature } from '@app/order-list/store/order-list.slice';
import { MerchantEffects } from '@merchant/store/merchant.effects';
import { MerchantFeature } from '@merchant/store/merchant.slice';
import { NewsletterBlockComponent } from '@shared/components/newsletter-block/newsletter-block.component';
import { SelectFieldComponent } from '@shared/components/select-field/select-field.component';
import { DefaultImagePipe } from '@shared/pipes/default-image.pipe';

import { AdBannerComponent } from './components/ad-banner/ad-banner.component';
import { AdBannerEffects } from './components/ad-banner/store/ad-banner.effects';
import { AdBannerFeature } from './components/ad-banner/store/ad-banner.slice';
import { AddressSubformComponent } from './components/address-subform/address-subform.component';
import { AddressComponent } from './components/address/address.component';
import { AuthenticationContainerComponent } from './components/authentication-container/authentication-container.component';
import { BoolFieldComponent } from './components/bool-field/bool-field.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { ChoiceDialogComponent } from './components/choice-dialog/choice-dialog.component';
import { CustomerSegmentSubformComponent } from './components/customer-segment-subform/customer-segment-subform.component';
import { DataSheetTabsComponent } from './components/data-sheet-tabs/data-sheet-tabs.component';
import { DeleteButtonComponent } from './components/delete-button/delete-button.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { DrsFeeSignComponent } from './components/drs-fee-logo/drs-fee-logo.component';
import { EasyAddressSubformComponent } from './components/easy-address-subform/easy-address-subform.component';
import { EasyLocalizedSeoSubformComponent } from './components/easy-localized-seo-subform/easy-localized-seo-subform.component';
import { EasyLocalizedValueSubformComponent } from './components/easy-localized-value-subform/easy-localized-value-subform.component';
import { EasyOpeningHoursSubformComponent } from './components/easy-opening-hours-subform/easy-opening-hours-subform.component';
import { EasyPhoneSubformComponent } from './components/easy-phone-subform/easy-phone-subform.component';
import { EasyTimePickerComponent } from './components/easy-time-picker/easy-time-picker.component';
import { EmbedVideoComponent } from './components/embed-video/embed-video.component';
import { EasyCheckboxComponent } from './components/forms/components/easy-checkbox/easy-checkbox.component';
import { EasyInputComponent } from './components/forms/components/easy-input/easy-input.component';
import { EasySelectExpandableComponent } from './components/forms/components/easy-select-expandable/easy-select-expandable.component';
import { EasySelectComponent } from './components/forms/components/easy-select/easy-select.component';
import { EasyTextareaComponent } from './components/forms/components/easy-textarea/easy-textarea.component';
import { GeneralSearchInputComponent } from './components/general-search-input/general-search-input.component';
import { GlobalLoaderComponent } from './components/global-loader/global-loader.component';
import { ImagePickerLegacyComponent } from './components/image-picker-legacy/image-picker-legacy.component';
import { ImagePickerComponent } from './components/image-picker/image-picker.component';
import { ImageSwiperComponent } from './components/image-swiper/image-swiper.component';
import { ImagesTabForProductsComponent } from './components/images-tab-for-products/images-tab-for-products.component';
import { ListTableComponent } from './components/list-table/list-table.component';
import { LocalizedKeyValuePairsSubformComponent } from './components/localized-key-value-pairs-subform/localized-key-value-pairs-subform.component';
import { LocalizedMarketingSubformComponent } from './components/localized-marketing-subform/localized-marketing-subform.component';
import { LocalizedSeoSubformComponent } from './components/localized-seo-subform/localized-seo-subform.component';
import { LocalizedTourismServiceSubformComponent } from './components/localized-tourism-service-subform/localized-tourism-service-subform.component';
import { LocalizedValueSubformComponent } from './components/localized-value-subform/localized-value-subform.component';
import { MainContentCardComponent } from './components/main-content-card/main-content-card.component';
import { MaintenanceBannerComponent } from './components/maintenance-banner/maintenance-banner.component';
import { MdReaderComponent } from './components/md-reader/md-reader.component';
import { NoEditRoleComponent } from './components/no-edit-role/no-edit-role.component';
import { OpeningHoursSubformComponent } from './components/opening-hours-subform/opening-hours-subform.component';
import { PhoneSubformComponent } from './components/phone-subform/phone-subform.component';
import { ProductDetailListTableComponent } from './components/product-detail-list-table/product-detail-list-table.component';
import { OperatingLicenseComponent } from './components/retailer-license-upload/operating-license.component';
import { SearchLayoutComponent } from './components/search-layout/search-layout.component';
import { SearchFieldDirective } from './components/search-layout/search-navbar/search-field.directive';
import { SearchNavbarComponent } from './components/search-layout/search-navbar/search-navbar.component';
import { FilterStoreEffects } from './components/search-layout/search-navbar/store/filter-store.effects';
import { FilterStoreFeature } from './components/search-layout/search-navbar/store/filter-store.slice';
import { SnackbarComponent } from './components/snackbar/snackbar.component';
import { StatusChipComponent } from './components/status-chip/status-chip.component';
import { CheckableColumnModule } from './components/table/checkable-column/checkable-column.module';
import { TabsComponent } from './components/tabs/tabs.component';
import { TextFieldComponent } from './components/text-field/text-field.component';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { HunDateAdapter } from './date-adapters/hun-date-adapter';
import { StoreFilterDialogComponent } from './dialogs/store-filter-dialogs/store-filter-dialog/store-filter-dialog.component';
import { StoredFiltersDialogComponent } from './dialogs/store-filter-dialogs/stored-filters-dialog/stored-filters-dialog.component';
import { EditableWithRoleDirective } from './directives/editable-with-role.directive';
import { IfHasRolesDirective } from './directives/if-has-roles.directive';
import { LimitDecimalDigitsDirective } from './directives/limit-decimal-digits.directive';
import { ScrollToInvalidControlDirective } from './directives/scroll-to-invalid-control.directive';
import { MaterialModule } from './material/material.module';
import { PartnerSearchModalComponent } from './modals/partner-search-modal/partner-search-modal.component';
import { HighlightByStartPipe } from './pipes/highlight-by-start.pipe';
import { HighlightPipe } from './pipes/highlight.pipe';
import { JoinPipe } from './pipes/join.pipe';
import { OriginalPricePipe } from './pipes/original-price.pipe';
import { PricePipe } from './pipes/price.pipe';
import { SafeHtmlPipe } from './pipes/safeHtml.pipe';
import { SortByPipe } from './pipes/sort-by.pipe';
import { SortMerchantTypesPipe } from './pipes/sort-merchant-types.pipe';
import { TelephoneFormatPipe } from './pipes/telephone-format.pipe';
import { ToDay } from './pipes/to-day.pipe';
import { ToMonthAndDay } from './pipes/to-month-and-day.pipe';
import { ToRegionPipe } from './pipes/toRegion.pipe';
import { ToSelectOptionPipe } from './pipes/toSelectOptions.pipe';
import { YesNoPipe } from './pipes/yes-no.pipe';
import { StatementDialogComponent } from './statement-dialog/statement-dialog.component';

registerLocaleData(localeHu);

const importedModules = [
  CommonModule,
  MaterialModule,
  ReactiveFormsModule,
  RouterModule,
  TranslateModule,
  FlexLayoutModule,
  NgxMatSelectSearchModule,
  FormsModule,
  SwiperModule,
  CheckableColumnModule,
  LetDirective,
];

const declarations = [
  // components
  TabsComponent,
  SnackbarComponent,
  DialogComponent,
  ChoiceDialogComponent,
  ImagePickerLegacyComponent,
  AddressComponent,
  AddressSubformComponent,
  EasyAddressSubformComponent,
  OpeningHoursSubformComponent,
  EasyOpeningHoursSubformComponent,
  TimePickerComponent,
  EasyTimePickerComponent,
  LocalizedValueSubformComponent,
  EasyLocalizedValueSubformComponent,
  LocalizedKeyValuePairsSubformComponent,
  LocalizedSeoSubformComponent,
  EasyLocalizedSeoSubformComponent,
  LocalizedMarketingSubformComponent,
  LocalizedTourismServiceSubformComponent,
  ImagePickerComponent,
  SelectFieldComponent,
  EmbedVideoComponent,
  NoEditRoleComponent,
  DeleteButtonComponent,
  PhoneSubformComponent,
  EasyPhoneSubformComponent,
  MdReaderComponent,
  ImageSwiperComponent,
  NewsletterBlockComponent,
  ListTableComponent,
  DataSheetTabsComponent,
  ImagesTabForProductsComponent,
  GeneralSearchInputComponent,
  ProductDetailListTableComponent,
  TextFieldComponent,
  BoolFieldComponent,
  StatementDialogComponent,
  SearchLayoutComponent,
  SearchNavbarComponent,
  StoreFilterDialogComponent,
  StoredFiltersDialogComponent,
  GlobalLoaderComponent,
  StatusChipComponent,
  AuthenticationContainerComponent,
  CustomerSegmentSubformComponent,
  MainContentCardComponent,
  BreadcrumbComponent,
  OperatingLicenseComponent,
  AdBannerComponent,
  MaintenanceBannerComponent,
  DrsFeeSignComponent,

  // modal
  PartnerSearchModalComponent,

  // form
  EasyInputComponent,
  EasySelectComponent,
  EasySelectExpandableComponent,
  EasyTextareaComponent,
  EasyCheckboxComponent,

  // directives
  ScrollToInvalidControlDirective,
  EditableWithRoleDirective,
  IfHasRolesDirective,
  LimitDecimalDigitsDirective,
  SearchFieldDirective,

  // pipes
  YesNoPipe,
  JoinPipe,
  PricePipe,
  SafeHtmlPipe,
  DefaultImagePipe,
  SortByPipe,
  SortMerchantTypesPipe,
  ToSelectOptionPipe,
  ToRegionPipe,
  OriginalPricePipe,
  TelephoneFormatPipe,
  ToMonthAndDay,
  ToDay,
  HighlightPipe,
  HighlightByStartPipe,
];

const exportedModules = [...declarations, ...importedModules];

@NgModule({
  declarations: declarations,
  imports: [
    importedModules,
    MarkdownModule.forRoot({
      loader: HttpClient, // optional, only if you use [src] attribute
      markedOptions: {
        provide: MarkedOptions,
        useValue: {
          gfm: true,
          breaks: false,
          pedantic: false,
          smartLists: true,
          smartypants: false,
        },
      },
    }),

    // stores
    StoreModule.forFeature(OrderListFeature),
    StoreModule.forFeature(FilterStoreFeature),
    StoreModule.forFeature(MerchantFeature),
    StoreModule.forFeature(AdBannerFeature),
    EffectsModule.forFeature([
      OrderListEffects,
      FilterStoreEffects,
      MerchantEffects,
      AdBannerEffects,
    ]),
  ],
  exports: exportedModules,
  providers: [
    { provide: DateAdapter, useClass: HunDateAdapter },
    { provide: LOCALE_ID, useValue: 'hu' },
    CurrencyPipe,
    DecimalPipe,
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
    };
  }
}
